/**
 * Environment configuration based on build-time environment variables
 */

import type { EnvironmentKey } from '@/types/environment'

export interface EnvironmentOption {
  value: EnvironmentKey
  label: string
}

export interface EntityConfig {
  name: string
  [key: string]: any
}

// Get enabled environments from build-time env var
const enabledEnvs: string[] = import.meta.env.VITE_ENABLED_ENVIRONMENTS?.split(',') || ['dev', 'test', 'uat', 'live']

// Get excluded entities from build-time env var
const excludedEntities: string[] = import.meta.env.VITE_EXCLUDED_ENTITIES?.split(',').map((e: string) => e.trim()) || []

const allEnvironments: EnvironmentOption[] = [
  { value: 'dev', label: 'Development' },
  { value: 'test', label: 'Test' },
  { value: 'uat', label: 'UAT' },
  { value: 'live', label: 'Production' }
]

export const AVAILABLE_ENVIRONMENTS: EnvironmentOption[] = allEnvironments.filter((env) => enabledEnvs.includes(env.value))

export const getAccessibleEnvironments = (userGroups: string[]): EnvironmentOption[] => {
  const hasRecognizedGroup = userGroups.some((group) => 
    ['admin', 'deployment', 'developer', 'readonly'].includes(group)
  )
  
  if (!hasRecognizedGroup) {
    // Users without recognized groups can only access dev and test
    return AVAILABLE_ENVIRONMENTS.filter((env) => ['dev', 'test'].includes(env.value))
  }
  
  return AVAILABLE_ENVIRONMENTS
}

export const isEnvironmentEnabled = (envValue: string): boolean => {
  return enabledEnvs.includes(envValue)
}

export const canAccessEnvironment = (envValue: string, userGroups: string[]): boolean => {
  const hasRecognizedGroup = userGroups.some((group) => 
    ['admin', 'deployment', 'developer', 'readonly'].includes(group)
  )
  
  if (!hasRecognizedGroup) {
    return ['dev', 'test'].includes(envValue)
  }
  
  return isEnvironmentEnabled(envValue)
}

export const getDefaultEnvironment = (): string => {
  return AVAILABLE_ENVIRONMENTS[0]?.value || 'dev'
}

export const isEntityEnabled = (entityName: string): boolean => {
  return !excludedEntities.includes(entityName)
}

export const getEnabledEntities = (allEntities: EntityConfig[]): EntityConfig[] => {
  return allEntities.filter((entity) => isEntityEnabled(entity.name))
}
